import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Sparkles, Send, ChevronRight } from "lucide-react"
import { ScrollArea } from "@/components/ui/scroll-area"

interface AiAssistantSidebarProps {
  isOpen: boolean
  onToggle: () => void
}

export function AiAssistantSidebar({ isOpen, onToggle }: AiAssistantSidebarProps) {
  const [input, setInput] = useState("")
  const [messages, setMessages] = useState<Array<{ role: "user" | "assistant"; content: string }>>([])
  const [isTyping, setIsTyping] = useState(false)

  const examplePrompts = [
    "Add a date picker field",
    "Create a multi-step form",
    "Add validation rules",
    "Group fields in sections",
    "Add a file upload field",
    "Create a horizontal layout",
  ]

  const handleSend = () => {
    if (!input.trim()) return

    const userMessage = { role: "user" as const, content: input }
    setMessages((prev) => [...prev, userMessage])
    setInput("")
    setIsTyping(true)

    setTimeout(() => {
      const assistantMessage = {
        role: "assistant" as const,
        content: "I can help you modify the form schema. This is a placeholder response.",
      }
      setMessages((prev) => [...prev, assistantMessage])
      setIsTyping(false)
    }, 1500)
  }

  const handlePromptClick = (prompt: string) => {
    setInput(prompt)
  }

  return (
    <div
      className={`border-l border-border bg-background flex flex-col transition-all duration-300 ${
        isOpen ? "w-96" : "w-12"
      }`}
    >
      <div className="border-b border-border p-2 flex items-center justify-between">
        {isOpen && (
          <div className="flex items-center gap-2 px-2">
            <div className="relative">
              <Sparkles className="h-4 w-4 text-primary animate-pulse" />
              <div className="absolute -top-1 -right-1 h-1.5 w-1.5 bg-green-500 rounded-full border border-background" />
            </div>
            <span className="font-semibold text-sm">AI Assistant</span>
          </div>
        )}
        <Button variant="ghost" size="icon" onClick={onToggle} className="h-8 w-8">
          <ChevronRight className={`h-4 w-4 transition-transform ${isOpen ? "" : "rotate-180"}`} />
        </Button>
      </div>

      {isOpen && (
        <>
          <ScrollArea className="flex-1 p-4">
            <div className="space-y-3">
              {messages.length === 0 ? (
                <div className="text-center py-8">
                  <Sparkles className="h-10 w-10 text-primary mx-auto mb-3 opacity-50" />
                  <p className="text-xs text-muted-foreground mb-4">Ask me to help you build your form</p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {examplePrompts.map((prompt, index) => (
                      <Button
                        key={index}
                        variant="outline"
                        size="sm"
                        onClick={() => handlePromptClick(prompt)}
                        className="text-xs h-7"
                      >
                        {prompt}
                      </Button>
                    ))}
                  </div>
                </div>
              ) : (
                messages.map((message, index) => (
                  <div key={index} className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[85%] rounded-lg px-3 py-2 ${
                        message.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"
                      }`}
                    >
                      <p className="text-xs">{message.content}</p>
                    </div>
                  </div>
                ))
              )}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-muted rounded-lg px-3 py-2">
                    <div className="flex gap-1">
                      <div className="w-1.5 h-1.5 bg-foreground/40 rounded-full animate-bounce [animation-delay:-0.3s]" />
                      <div className="w-1.5 h-1.5 bg-foreground/40 rounded-full animate-bounce [animation-delay:-0.15s]" />
                      <div className="w-1.5 h-1.5 bg-foreground/40 rounded-full animate-bounce" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          <div className="border-t border-border p-3">
            <div className="flex gap-2">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault()
                    handleSend()
                  }
                }}
                placeholder="Describe what you want..."
                className="resize-none min-h-[60px] text-sm"
              />
              <Button onClick={handleSend} size="icon" className="shrink-0 h-auto">
                <Send className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
